import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, Play, RefreshCw, Upload } from '../../lib/icons';
import { fmt } from '../shared/badges';
import { DEFAVORABLE, NEUTRE_DOUX } from '../../lib/couleurs';
import {
  TYPES_PIECE_LIBELLE,
  deposerDocument,
  fetchDriveConnexion,
  lancerSyncDrive,
  majDriveConnexion,
  obtenirUrlConsentement,
  reprendreErp,
} from '../../lib/passerelle';
import { Carte, btn, btnLight, inputCls, labelCls, td, th } from '../cri/ui';

export const DepotPasserelle = ({ onMaj }) => {
  const [fichiers, setFichiers] = useState([]);
  const [canal, setCanal] = useState('web');
  const [envoi, setEnvoi] = useState(false);
  const [resultats, setResultats] = useState([]);

  const envoyer = async () => {
    if (!fichiers.length) return toast.error('Choisissez au moins un fichier.');
    setEnvoi(true);
    const sorties = [];
    for (const f of fichiers) {
      const { error, data } = await deposerDocument(f, { canal, deposant: 'gérant' });
      sorties.push({ nom: f.name, error, ...(data ?? {}) });
    }
    setEnvoi(false);
    setResultats(sorties);
    setFichiers([]);
    const ok = sorties.filter((s) => !s.error).length;
    toast.success(`${ok} document(s) archivé(s) et mis en file de traitement.`);
    onMaj();
  };

  return (
    <Carte
      testid="passerelle-depot"
      titre="Dépôt documentaire (web / photo mobile)"
      sous="Même chaîne serveur que le canal Drive : archivage de l’original, extraction, anti-doublon, puis création ou rattachement."
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="block sm:col-span-2">
          <span className={labelCls}>Fichiers (PDF, photo, Excel, CSV)</span>
          <input
            data-testid="passerelle-fichiers"
            type="file"
            multiple
            onChange={(e) => setFichiers(Array.from(e.target.files ?? []))}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className={labelCls}>Canal</span>
          <select data-testid="passerelle-canal" value={canal} onChange={(e) => setCanal(e.target.value)} className={inputCls}>
            <option value="web">Saisie web</option>
            <option value="mobile">Photo mobile</option>
          </select>
        </label>
      </div>
      <button data-testid="passerelle-envoyer" type="button" onClick={envoyer} disabled={envoi} className={`${btn} mt-3`}>
        <Upload size={15} /> {envoi ? 'Envoi en cours…' : `Déposer ${fichiers.length || ''} document(s)`}
      </button>

      {resultats.length > 0 && (
        <div className="mt-4 overflow-x-auto border border-slate-200">
          <table className="w-full min-w-[620px] border-collapse">
            <thead className="bg-slate-50">
              <tr>
                <th className={th}>Fichier</th>
                <th className={th}>Tâche</th>
                <th className={th}>Empreinte</th>
                <th className={th}>Fichier déjà connu</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {resultats.map((r) => (
                <tr key={r.nom} data-testid={`passerelle-depot-res-${r.nom}`}>
                  <td className={td}>{r.nom}</td>
                  <td className={`${td} ${r.error ? DEFAVORABLE : ''}`}>{r.error ?? r.job_id}</td>
                  <td className={`${td} ${NEUTRE_DOUX}`}>{r.sha256 ? `${r.sha256.slice(0, 12)}…` : '—'}</td>
                  <td className={td}>{r.fichier_deja_connu ? 'Oui — aucune réanalyse' : 'Non'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Carte>
  );
};

export const ConnexionDrive = ({ onMaj }) => {
  const [cnx, setCnx] = useState(null);
  const [dossier, setDossier] = useState('');
  const [drivePartage, setDrivePartage] = useState('');
  const [sousDossiers, setSousDossiers] = useState(true);

  const charger = useCallback(() => {
    fetchDriveConnexion().then((c) => {
      setCnx(c);
      setDossier(c?.dossier_id ?? '');
      setDrivePartage(c?.drive_partage_id ?? '');
      setSousDossiers(c?.sous_dossiers ?? true);
    });
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  const enregistrer = async (actif) => {
    const { error } = await majDriveConnexion({ dossier_id: dossier, drive_partage_id: drivePartage || null, sous_dossiers: sousDossiers, actif });
    if (error) return toast.error(error);
    toast.success('Périmètre Drive enregistré.');
    charger();
    onMaj();
  };

  const synchroniser = async () => {
    const { error } = await lancerSyncDrive();
    if (error) return toast.error(error);
    toast.success('Synchronisation demandée.');
    onMaj();
  };

  const reprise = async () => {
    const { error, data } = await reprendreErp();
    if (error) return toast.error(error);
    const collisions = data?.collisions?.length ?? 0;
    toast.success(
      `Reprise ERP : ${data?.reprises ?? 0} pièce(s) reprise(s), ${data?.deja_reprises ?? 0} déjà connue(s), ${collisions} collision(s) signalée(s).`
    );
    onMaj();
  };

  return (
    <Carte
      testid="passerelle-connexion"
      titre="Connexion Google Drive"
      sous="Les secrets restent côté serveur. Aucune connexion n’est annoncée opérationnelle avant d’avoir été vérifiée."
      action={
        <div className="flex flex-wrap gap-2">
          <button data-testid="passerelle-sync" type="button" onClick={synchroniser} className={btnLight}>
            <RefreshCw size={13} /> Synchroniser
          </button>
          <button data-testid="passerelle-reprise" type="button" onClick={reprise} className={btnLight}>
            <Download size={13} /> Reprendre les pièces ERP
          </button>
        </div>
      }
    >
      <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
        {[
          ['État', cnx?.reconnexion_requise ? 'reconnexion Google requise' : cnx?.etat ?? '—'],
          ['Clé de compte de service', cnx?.compte_service_present ? 'Présente' : 'Absente (connecteur non vérifié)'],
          ['Curseur de changements', cnx?.curseur_present ? 'Actif' : 'Non initialisé'],
          ['Dernière synchronisation réussie', cnx?.derniere_sync_reussie ? String(cnx.derniere_sync_reussie).slice(0, 19).replace('T', ' ') : 'Jamais'],
        ].map(([k, v]) => (
          <div key={k} className="bg-white px-3 py-2.5">
            <p className="text-[11px] text-slate-500">{k}</p>
            <p className="font-data text-sm font-semibold text-slate-900">{v}</p>
          </div>
        ))}
      </div>

      {cnx?.reconnexion_requise && (
        <div data-testid="passerelle-reconnexion-requise" className="mt-3 border border-red-200 bg-red-50 px-3 py-2.5">
          <p className={`text-sm font-semibold ${DEFAVORABLE}`}>Reconnexion Google requise</p>
          <p className="mt-1 text-xs text-slate-700">
            L’autorisation Google de {cnx.oauth_compte ?? 'ce compte'} a expiré ou a été révoquée
            {cnx.oauth_erreur_le ? ` (constaté le ${String(cnx.oauth_erreur_le).slice(0, 19).replace('T', ' ')})` : ''}. Les
            synchronisations automatiques sont suspendues ; le dossier surveillé, les documents déjà archivés, la file de
            traitement et l’anti-doublon sont conservés.
          </p>
          <button
            data-testid="passerelle-reconnecter-drive"
            type="button"
            onClick={async () => {
              const { error, data } = await obtenirUrlConsentement();
              if (error) return toast.error(error);
              window.open(data.url, '_blank', 'noopener');
              toast.success('Consentement Google ouvert dans un nouvel onglet.');
            }}
            className={`${btn} mt-2`}
          >
            Reconnecter Google Drive
          </button>
        </div>
      )}

      {!cnx?.compte_service_present && (
        <p data-testid="passerelle-drive-avertissement" className="mt-3 border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Connecteur Drive <strong>non vérifié</strong> : la clé de compte de service Google n’est pas installée sur le serveur.
          Le dépôt web et la photo mobile utilisent déjà la même chaîne de traitement, sans aucune simulation.
        </p>
      )}

      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="block">
          <span className={labelCls}>ID du dossier surveillé</span>
          <input data-testid="passerelle-dossier" value={dossier} onChange={(e) => setDossier(e.target.value)} className={inputCls} placeholder="1AbC…" />
        </label>
        <label className="block">
          <span className={labelCls}>Drive partagé (facultatif)</span>
          <input data-testid="passerelle-drive-partage" value={drivePartage} onChange={(e) => setDrivePartage(e.target.value)} className={inputCls} />
        </label>
        <label className="mt-6 flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={sousDossiers} onChange={(e) => setSousDossiers(e.target.checked)} />
          Inclure les sous-dossiers autorisés
        </label>
        <div className="mt-5 flex gap-2">
          <button data-testid="passerelle-enregistrer-cnx" type="button" onClick={() => enregistrer(false)} className={btnLight}>
            Enregistrer
          </button>
          <button data-testid="passerelle-activer-cnx" type="button" onClick={() => enregistrer(true)} className={btnLight}>
            <Play size={13} /> Activer
          </button>
        </div>
      </div>
      <p className="mt-2 text-[11px] text-slate-500">
        La comptable n’a rien à renommer ni à classer : tout fichier déposé dans le périmètre est traité. Un renommage ne crée pas
        de nouvelle pièce, une modification de contenu crée une nouvelle version, et une suppression dans Drive ne supprime aucune écriture.
      </p>
    </Carte>
  );
};

export const JournalPasserelle = ({ journal }) => (
  <Carte testid="passerelle-journal" titre="Historique des décisions" sous="Règle utilisée, objets sources, montants, date et auteur (automatique ou humain).">
    <div className="max-h-[560px] overflow-auto border border-slate-200">
      <table className="w-full min-w-[820px] border-collapse">
        <thead className="sticky top-0 bg-slate-50">
          <tr>
            <th className={th}>Date</th>
            <th className={th}>Action</th>
            <th className={th}>Objet</th>
            <th className={th}>Règle</th>
            <th className={th}>Auteur</th>
            <th className={th}>Montant</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {journal.length === 0 && (
            <tr>
              <td className={`${td} text-slate-500`} colSpan={6}>
                Aucune décision enregistrée.
              </td>
            </tr>
          )}
          {journal.map((j) => (
            <tr key={j._id} data-testid={`passerelle-journal-${j._id}`}>
              <td className={td}>{String(j.le).slice(0, 19).replace('T', ' ')}</td>
              <td className={`${td} font-semibold`}>{j.action}</td>
              <td className={`${td} ${NEUTRE_DOUX}`}>
                {j.objet_type} {j.objet_id ? `· ${String(j.objet_id).slice(0, 28)}` : ''}
              </td>
              <td className={`${td} ${NEUTRE_DOUX}`}>{j.regle || '—'}</td>
              <td className={td}>
                {j.auteur}
                {j.automatique ? ' (auto)' : ''}
              </td>
              <td className={td}>
                {j.detail?.montant_millimes != null ? `${fmt(j.detail.montant_millimes / 1000, 2)} TND` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Carte>
);

const ETATS_PIECE = {
  enregistre: 'Enregistrée',
  repris_erp: 'Reprise de l’ERP',
  annule: 'Annulée',
  remplace: 'Remplacée',
};

// Un échéancier de leasing ne porte ni numéro ni date de pièce : sa période
// d'engagement remplace la colonne Date, sinon la ligne paraîtrait vide.
const dateOuPeriode = (p) => {
  if (p.date_piece) return p.date_piece;
  const d = p.periode?.debut;
  const f = p.periode?.fin;
  if (d || f) return `Du ${d ?? '?'} au ${f ?? '?'}`;
  return '—';
};

export const PiecesPasserelle = ({ pieces, obligations, chargement = false }) => (
  <div className="space-y-4">
    <Carte
      testid="passerelle-pieces"
      titre={`Pièces métier créées (${pieces.length})`}
      sous="Identité canonique, montants en millimes, justificatifs rattachés et référence ERP."
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[980px] border-collapse">
          <thead className="bg-slate-50">
            <tr>
              <th className={th}>Nature</th>
              <th className={th}>Émetteur</th>
              <th className={th}>Numéro</th>
              <th className={th}>Date / période</th>
              <th className={th}>Total</th>
              <th className={th}>Devise</th>
              <th className={th}>Justificatifs</th>
              <th className={th}>Statut</th>
              <th className={th}>Référence ERP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pieces.length === 0 && (
              <tr>
                <td className={`${td} text-slate-500`} colSpan={9}>
                  {chargement ? 'Lecture des pièces métier…' : 'Aucune pièce métier enregistrée par la passerelle.'}
                </td>
              </tr>
            )}
            {pieces.map((p) => (
              <tr key={p._id} data-testid={`passerelle-piece-${p._id}`}>
                <td className={td}>{TYPES_PIECE_LIBELLE[p.type_piece] ?? p.type_piece}</td>
                <td className={td}>
                  {p.issuer_nom ?? p.issuer_party_id ?? '—'}
                  {p.issuer_nom && p.issuer_party_id && (
                    <span className={`block font-data text-[10px] ${NEUTRE_DOUX}`}>{p.issuer_party_id}</span>
                  )}
                </td>
                <td className={td}>{p.numero ?? '—'}</td>
                <td className={td}>{dateOuPeriode(p)}</td>
                <td className={`${td} font-semibold`}>{p.total_tnd != null ? fmt(p.total_tnd, 2) : '—'}</td>
                <td className={`${td} ${NEUTRE_DOUX}`}>{p.devise}</td>
                <td className={td}>{p.justificatifs}</td>
                <td className={`${td} ${NEUTRE_DOUX}`}>{ETATS_PIECE[p.statut] ?? p.statut}</td>
                <td className={`${td} ${NEUTRE_DOUX}`}>{p.reference_erp?.numero ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Carte>

    <Carte
      testid="passerelle-obligations"
      titre={`Obligations générées (${obligations.length})`}
      sous="Une facture et sa traite ne comptent jamais deux fois : la traite porte le calendrier de la dette existante. Un échéancier reste un calendrier prévisionnel et ne crée aucune dette."
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] border-collapse">
          <thead className="bg-slate-50">
            <tr>
              <th className={th}>Nature</th>
              <th className={th}>Tiers</th>
              <th className={th}>Origine</th>
              <th className={th}>Échéance</th>
              <th className={th}>Montant</th>
              <th className={th}>Reste</th>
              <th className={th}>État</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {obligations.length === 0 && (
              <tr>
                <td className={`${td} text-slate-500`} colSpan={7}>
                  {chargement ? 'Lecture des obligations…' : 'Aucune obligation enregistrée.'}
                </td>
              </tr>
            )}
            {obligations.map((o) => (
              <tr key={o._id} data-testid={`passerelle-obligation-${o._id}`}>
                <td className={td}>{o.nature}</td>
                <td className={td}>{o.party_nom ?? o.party_id}</td>
                <td className={`${td} ${NEUTRE_DOUX}`}>{o.origine_id}</td>
                <td className={td}>{o.echeance ?? '—'}</td>
                <td className={`${td} ${DEFAVORABLE}`}>{fmt(o.montant_tnd ?? 0, 2)}</td>
                <td className={`${td} font-semibold`}>{fmt(o.reste_tnd ?? 0, 2)}</td>
                <td className={`${td} ${NEUTRE_DOUX}`}>{o.etat}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Carte>
  </div>
);
